import type { LinkProps } from "@tanstack/react-router";
import {
  ROUTE_BREADCRUMB_LABELS,
  ROUTE_BREADCRUMB_PARENTS,
  SEGMENT_LABELS,
} from "../data/route-breadcrumb-overrides";
import { sidebarRouteIndex } from "../data/sidebar-route-index";

export const HOME_ROUTE = "/app/dashboard" satisfies LinkProps["to"];

export type BreadcrumbCrumb = {
  label: string;
  to?: LinkProps["to"];
  params?: Record<string, string>;
};

function labelForRouteId(routeId: string): string {
  if (ROUTE_BREADCRUMB_LABELS[routeId]) {
    return ROUTE_BREADCRUMB_LABELS[routeId];
  }
  const fromSidebar = sidebarRouteIndex[routeId];
  if (fromSidebar) return fromSidebar.title;

  const last = routeId.split("/").pop() ?? "";
  if (SEGMENT_LABELS[last]) return SEGMENT_LABELS[last];
  if (last.startsWith("$")) return SEGMENT_LABELS.$id ?? "ລາຍລະອຽດ";
  return last;
}

function parentRouteId(routeId: string): string | undefined {
  if (routeId === HOME_ROUTE) return undefined;

  const explicit = ROUTE_BREADCRUMB_PARENTS[routeId];
  if (explicit) return explicit;

  if (routeId.endsWith("/new")) return routeId.slice(0, -4);
  if (routeId.endsWith("/edit")) return routeId.slice(0, -5);
  if (routeId.endsWith("/pdf-preview")) {
    return routeId.slice(0, -"/pdf-preview".length);
  }
  if (routeId.endsWith("/preview")) return routeId.slice(0, -"/preview".length);
  if (routeId.endsWith("/versions")) return routeId.slice(0, -"/versions".length);
  if (routeId.endsWith("/analysis")) return routeId.slice(0, -"/analysis".length);

  if (routeId.endsWith("/$submissionId")) {
    return routeId.slice(0, -"/$submissionId".length);
  }
  if (routeId.endsWith("/$executionId")) {
    return routeId.slice(0, -"/$executionId".length);
  }
  if (routeId.endsWith("/$employeeId")) {
    return routeId.slice(0, -"/$employeeId".length);
  }
  if (routeId.endsWith("/$userId")) {
    return routeId.slice(0, -"/$userId".length);
  }
  if (routeId.endsWith("/$id")) {
    return routeId.slice(0, -"/$id".length);
  }

  const parts = routeId.split("/").filter(Boolean);
  if (parts.length <= 2) return HOME_ROUTE;
  parts.pop();
  const parent = `/${parts.join("/")}`;
  return parent.length > "/app".length ? parent : HOME_ROUTE;
}

function buildRouteIdChain(routeId: string): string[] {
  const chain: string[] = [];
  const seen = new Set<string>();
  let current: string | undefined = routeId;

  for (let i = 0; i < 12 && current; i++) {
    if (seen.has(current)) break;
    seen.add(current);
    chain.unshift(current);
    if (current === HOME_ROUTE) break;
    current = parentRouteId(current);
  }

  return chain;
}

export function resolveCrumbLink(
  routeId: string,
  params: Record<string, string | undefined>,
): Pick<BreadcrumbCrumb, "to" | "params"> {
  if (!routeId.includes("$")) {
    return { to: routeId as LinkProps["to"] };
  }

  const routeParams: Record<string, string> = {};
  for (const match of routeId.matchAll(/\$(\w+)/g)) {
    const key = match[1];
    const value = params[key];
    if (!value) return {};
    routeParams[key] = value;
  }

  return {
    to: routeId as LinkProps["to"],
    params: routeParams,
  };
}

export function resolveBreadcrumbTrail(
  routeId: string,
  params: Record<string, string | undefined>,
  lastLabelOverride?: string,
): BreadcrumbCrumb[] {
  if (!routeId.startsWith("/app")) return [];

  const chain = buildRouteIdChain(routeId);
  if (chain.length === 0) return [];

  const crumbs: BreadcrumbCrumb[] = [];

  if (routeId !== HOME_ROUTE) {
    crumbs.push({ label: "ແຜງຄວບຄຸມ", to: HOME_ROUTE });
  }

  const leafSidebar = sidebarRouteIndex[routeId];
  if (
    leafSidebar &&
    leafSidebar.groupTitle !== "ທົ່ວໄປ" &&
    routeId !== HOME_ROUTE
  ) {
    crumbs.push({ label: leafSidebar.groupTitle });
  }

  chain.forEach((id, index) => {
    const isLast = index === chain.length - 1;
    const label = isLast
      ? (lastLabelOverride ?? labelForRouteId(id))
      : labelForRouteId(id);

    if (isLast) {
      crumbs.push({ label });
      return;
    }

    crumbs.push({ label, ...resolveCrumbLink(id, params) });
  });

  return crumbs;
}
